import { useForm, SubmitHandler } from 'react-hook-form';
import FormModal from '@/components/common/FormModal';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronDownIcon } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { IProduct } from '@/services/OrderManagement/types';
import { useGetOrderCancellationReasons, useCancelOrderMutation } from '@/services/OrderManagement';
import useCustomToast from '@/components/common/Toast'; // Import the custom toast hook

export interface ICancelOrderModalVariantImages {
  id: number;
  image_small: string;
  is_primary: boolean;
}

interface EditPersonalInformationModalProps {
  amount: string;
  isOpen: boolean;
  orderId: string;
  product: IProduct;
  variantImages: ICancelOrderModalVariantImages[];
  onClose: () => void;
}

export interface ICancellationReasonFormData {
  reason_id: string;
  account_name?: string;
  account_number?: string;
  bank_name?: string;
  bank_branch?: string;
}

interface ErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface CancelOrderPayload {
  reason_id: string;
  order_tracking_id: string;
  amount: number;
}

const validationSchema = Yup.object({
  reason_id: Yup.string().required('Reason is required'),
});

const CancelOrderModal: React.FC<EditPersonalInformationModalProps> = ({
  amount,
  isOpen,
  orderId,
  onClose,
  product,
  variantImages,
}) => {
  const { data: cancellationReasons } = useGetOrderCancellationReasons();
  const { mutateAsync: cancelOrder, isLoading } = useCancelOrderMutation();
  const { showToast } = useCustomToast(); // Initialize the custom toast hook
  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ICancellationReasonFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reason_id: '',
    },
  });
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const { isOpen: isSelectOpen, onOpen, onClose: onSelectClose } = useDisclosure();

  const handleSelect = (value: string) => {
    setSelectedReason(value);
    setValue('reason_id', value);
  };

  const onSubmit: SubmitHandler<ICancellationReasonFormData> = async (data) => {
    try {
      const payload: CancelOrderPayload = {
        reason_id: data.reason_id,
        order_tracking_id: orderId,
        amount: parseFloat(amount),
      };

      const response = await cancelOrder(payload);

      showToast('success', response?.message || 'Your order has been successfully cancelled.');
      onClose();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showToast('error', err?.response?.data?.message || 'Failed to cancel order.');
    }
  };

  return (
    <FormModal
      isLoading={isLoading}
      saveText="Cancel Order"
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel Order"
      onConfirm={handleSubmit(onSubmit)}
      // Remove isSuccess and successContent since toast handles the success message
    >
      <Box fontFamily={'primary'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex gap={6} direction={'column'}>
            <Text fontSize={{ base: 'md', lg: 'xl' }} color={'text.primary'} fontWeight="semibold">
              Are you sure you want to cancel this order?
            </Text>
            <Flex gap={4} alignItems="center">
              <Image
                src={variantImages?.[0]?.image_small}
                alt="Product"
                boxSize="100px"
                borderRadius={'md'}
                objectFit="cover"
                objectPosition={'top'}
              />
              <Box>
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  color="text.primary"
                  fontFamily={'primary'}
                >
                  {product?.name}
                </Text>
                <Text fontSize="xs" color="text.secondary" fontFamily={'primary'} noOfLines={2}>
                  {product?.description}
                </Text>
              </Box>
            </Flex>
            <Divider borderColor={'border.default'} />
            <HStack justifyContent={'space-between'}>
              <Text fontSize={'sm'} color={'text.primary'} fontWeight={'semibold'}>
                Total Refund
              </Text>
              <Text fontSize={'sm'} color={'text.primary'} fontWeight={'semibold'}>
                ${amount}
              </Text>
            </HStack>
            <FormControl isInvalid={!!errors?.reason_id}>
              <FormLabel
                fontSize="sm"
                color={errors?.reason_id ? 'status.error' : 'text.secondary'}
              >
                Reason For Cancellation:
              </FormLabel>
              <Box ref={menuRef} position="relative">
                <Menu isOpen={isSelectOpen} onClose={onSelectClose}>
                  <MenuButton
                    as={Button}
                    rightIcon={
                      <ChevronDownIcon color={errors?.reason_id ? 'status.error' : '#090F1A'} />
                    }
                    w="full"
                    textAlign="left"
                    bg="white"
                    border={errors?.reason_id ? '2px solid' : '1px solid'}
                    borderColor={errors?.reason_id ? 'status.error' : 'border.default'}
                    _hover={{ bg: 'gray.100' }}
                    _focus={{
                      outline: 'none',
                      borderColor: errors?.reason_id ? 'status.error' : 'brand.primary',
                      boxShadow: errors?.reason_id
                        ? '0 0 0 1px status.error'
                        : '0 0 0 1px brand.primary',
                    }}
                    onClick={onOpen}
                    color={'text.primary'}
                  >
                    {selectedReason
                      ? cancellationReasons?.find((reason) => reason.id === selectedReason)?.reason
                      : 'Select Cancellation Reason'}
                  </MenuButton>
                  <MenuList
                    borderRadius="8px"
                    py="2"
                    w={`${menuRef.current?.offsetWidth}px`}
                    maxWidth="full"
                    boxShadow="lg"
                    zIndex="dropdown"
                    transition="all 0.2s ease-in-out"
                  >
                    {cancellationReasons?.map((reason, index) => (
                      <React.Fragment key={reason.id}>
                        <MenuItem
                          py="2"
                          background={'none'}
                          onClick={() => handleSelect(reason.id)}
                          color={selectedReason === reason.id ? 'text.primary' : 'text.secondary'}
                          _hover={{ bg: 'gray.100', color: 'text.primary' }}
                        >
                          {reason.reason}
                        </MenuItem>
                        {index < cancellationReasons.length - 1 && (
                          <MenuDivider borderColor="gray.200" />
                        )}
                      </React.Fragment>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
              {errors?.reason_id && (
                <Text fontSize="xs" color="status.error" mt={1}>
                  {errors.reason_id.message?.toString()}
                </Text>
              )}
            </FormControl>
          </Flex>
        </form>
      </Box>
    </FormModal>
  );
};

export default CancelOrderModal;
